import React, {useEffect, useState} from 'react';
import {EnumAuditValueType, IAuditRevisionResource, IPostAuditBody} from '../../../../resources/IChecklistAudit.resource';
import ReasoningButton from '../../../../components/ReasoningButton';
import ShortValueField from '../ShortValueField';
import styles from './styles.module.scss';

interface IProps {
    revisions: IAuditRevisionResource[];
    selectedChecklistUuid: string;
    onSave: (body: IPostAuditBody) => Promise<void>;
    companyId: number;
    phonecallUuid: string;
    checklistId: number;
}

function formatRevisionLabel(rev: IAuditRevisionResource): string {
    const author = rev.createdBy
        ? `${rev.createdBy.firstName} ${rev.createdBy.lastName}`.trim()
        : '';
    if (!rev.revision) return author ? `Нова — ${author}` : 'Нова';
    const date = new Date(rev.revision);
    const dateStr = isNaN(date.getTime()) ? rev.revision : date.toLocaleString('uk-UA');
    return author ? `${dateStr} — ${author}` : dateStr;
}

const ChecklistAuditForm: React.FC<IProps> = ({revisions, selectedChecklistUuid, onSave, companyId, phonecallUuid, checklistId}) => {
    const [selectedRevisionIndex, setSelectedRevisionIndex] = useState<number>(0);
    const [draft, setDraft] = useState<Record<string, string | null>>({});
    const [isSaving, setIsSaving] = useState<boolean>(false);

    useEffect(() => {
        setSelectedRevisionIndex(0);
    }, [revisions]);

    useEffect(() => {
        const current = revisions[selectedRevisionIndex];
        if (!current) {
            setDraft({});
            return;
        }
        const isDraft = !current.revision;
        const initial: Record<string, string | null> = {};
        current.cols.forEach((col) => {
            const {value, auditValue} = col.pair;
            if (col.valueType === EnumAuditValueType.SHORT) {
                const source = auditValue !== null ? auditValue : (isDraft ? value : null);
                initial[col.itemUuid] = source === null ? null : String(source);
            } else {
                initial[col.itemUuid] = auditValue !== null ? String(auditValue) : (isDraft && value !== null ? String(value) : '');
            }
        });
        setDraft(initial);
    }, [selectedRevisionIndex, revisions]);

    const currentRevision = revisions[selectedRevisionIndex];

    const isFormValid = currentRevision
        ? currentRevision.cols.every((col) => {
            if (col.valueType === EnumAuditValueType.SHORT) return true;
            return (draft[col.itemUuid] ?? '').trim() !== '';
        })
        : false;

    const handleRevisionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedRevisionIndex(Number(e.currentTarget.value));
    };

    const handleFieldChange = (itemUuid: string, value: string | null) => {
        setDraft((prev) => ({...prev, [itemUuid]: value}));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave({checklistUuid: selectedChecklistUuid, data: draft});
        } finally {
            setIsSaving(false);
        }
    };

    if (!currentRevision) {
        return (
            <div className={styles.audit}>
                <button
                    className={styles.audit__save}
                    type="button"
                    disabled={isSaving}
                    onClick={handleSave}
                >
                    {isSaving ? 'Збереження...' : 'Зберегти'}
                </button>
            </div>
        );
    }

    return (
        <div className={styles.audit}>
            {revisions.length > 0 && (
                <div className={styles.audit__revisions}>
                    <label className={styles.audit__label} htmlFor="audit-revision-select">
                        Ревізія:
                    </label>
                    <select
                        id="audit-revision-select"
                        className={styles.audit__revisionSelect}
                        value={selectedRevisionIndex}
                        onChange={handleRevisionChange}
                    >
                        {revisions.map((rev, index) => (
                            <option key={rev.revision ?? `draft-${index}`} value={index}>
                                {formatRevisionLabel(rev)}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className={styles.audit__cols}>
                {currentRevision.cols.map((col) => {
                    const refValue = col.pair.value;
                    const refLabel = col.valueType === EnumAuditValueType.SHORT
                        ? (refValue === null ? 'N/A' : Number(refValue) === 0 ? 'NO' : Number(refValue) === 1 ? 'YES' : String(refValue))
                        : (refValue !== null ? String(refValue) : '—');
                    return (
                        <div key={col.itemUuid} className={styles.audit__field}>
                            <label className={styles.audit__label}>({col.type}) {col.question}</label>
                            <span className={styles.audit__reference}>Harmonica:
                                <span className={styles.audit__reference_value}> {refLabel}</span></span>
                            <div className={styles.audit__fieldRow}>
                                {col.valueType === EnumAuditValueType.SHORT ? (
                                    <ShortValueField
                                        key={`${col.itemUuid}-${selectedRevisionIndex}`}
                                        value={draft[col.itemUuid] ?? null}
                                        onChange={(v) => handleFieldChange(col.itemUuid, v)}
                                    />
                                ) : (
                                    <textarea
                                        className={`${styles.audit__textarea} ${(draft[col.itemUuid] ?? '') === '' ? styles.audit__textarea_invalid : ''}`}
                                        value={draft[col.itemUuid] ?? ''}
                                        onChange={(e) => handleFieldChange(col.itemUuid, e.currentTarget.value)}
                                    />
                                )}
                                <ReasoningButton
                                    companyId={companyId}
                                    phonecallUuid={phonecallUuid}
                                    checklistId={checklistId}
                                    itemUuid={col.itemUuid}
                                    questionLabel={`(${col.type}) ${col.question}`}
                                    className={styles.audit__questionBtn}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            <button
                className={styles.audit__save}
                type="button"
                disabled={isSaving || !isFormValid}
                onClick={handleSave}
            >
                {isSaving ? 'Збереження...' : 'Зберегти'}
            </button>

        </div>
    );
};

export default ChecklistAuditForm;
